import { useState } from 'react';
import { Modal, Button, Group, Text, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { usePayDocumentMutation } from '@api/documents';
import { usePaymentMethods } from '@api/paymentMethods';
import { CommonHash } from '@types';
import Notification from '@components/notification/notification';
import { isDocumentPayable, titleCase } from '@utils';

interface PayDocumentProps {
  opened: boolean;
  close: () => void;
  row: CommonHash;
}

export const PayDocumentModal = ({ opened, close, row }: PayDocumentProps) => {
  const { mutate: payDocument, isLoading } = usePayDocumentMutation();
  const { data: paymentMethodsData } = usePaymentMethods();
  const isPayable = isDocumentPayable(row?.type, row?.status);
  const isPaid = row?.paid || false;
  const [paidAt, setPaidAt] = useState<Date | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);

  const paymentMethodOptions = (paymentMethodsData?.data ?? []).map((pm) => ({
    value: String(pm.id),
    label: titleCase(pm.name) ?? pm.name,
  }));

  const handleConfirmPay = () => {
    if (!isPayable && !isPaid) {
      Notification({
        type: 'error',
        message: 'Este tipo de documento no se puede marcar como pagado.',
      });
      close();
      return;
    }

    payDocument(
      {
        uuid: row.uuid as string,
        type: row.type as string,
        status: row.status as string,
        paid: !isPaid,
        paid_at: paidAt ? paidAt.toISOString().split('T')[0] : undefined,
        payment_method_id: paymentMethodId ? Number(paymentMethodId) : undefined,
      },
      {
        onSuccess: () => {
          setPaidAt(null);
          setPaymentMethodId(null);
          close();
        },
        onError: (error: any) => {
          Notification({
            type: 'error',
            message:
              'Error al marcar documento como pagado. ' +
              (error.response?.data?.error?.message || error.message || 'Intente nuevamente.'),
          });
        },
      },
    );
  };

  return (
    <Modal.Root size="md" opened={opened} onClose={close} centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title fz={20} fw={600}>
            Estado Pago del documento
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Text mb={20}>
            ¿Está seguro de que desea marcar el documento {row?.dte_type} N° {row?.number} como
            {isPaid ? ' no pagado' : ' pagado'}?
          </Text>
          {!isPaid && (
            <>
              <DatePickerInput
                label="Fecha de pago (opcional)"
                placeholder="Sin fecha"
                value={paidAt}
                onChange={setPaidAt}
                clearable
                mb={12}
              />
              <Select
                label="Método de pago (opcional)"
                placeholder="Seleccionar método"
                data={paymentMethodOptions}
                value={paymentMethodId}
                onChange={setPaymentMethodId}
                clearable
                mb={20}
              />
            </>
          )}
          <Group justify="end">
            <Button onClick={close} variant="outline">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmPay}
              loading={isLoading}
              color={isPaid ? 'red' : 'green'}
              disabled={!isPayable}
            >
              {isPaid ? 'Marcar como No Pagado' : 'Marcar como Pagado'}
            </Button>
          </Group>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
